import { ErrorHandler } from '@angular/core';
import { Params } from '@angular/router';

import { TabType } from 'app/services/tab/model';
import { IWizardStepUi } from 'components/form-wizard/model/form-wizard.interface';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import {
  getTypedControls,
  getTypedForm,
} from 'common/dynamic-form/utils/form-preparing';

import { deepEqual } from './deep-equal';
import { filterNotDefinedItems } from './filter-not-defined-items';

import {
  Func,
  ICart,
  ICheckbox,
  IControl,
  IDesktopDocument,
  IDocument,
  IErrorResponse,
  IField,
  IFilterDocument,
  IFormDocument,
  IFormPage,
  IListFilterBinder,
  IListOptionList,
  IMessageSet,
  INormalBanner,
  INotificationBanner,
  IRecord,
  ISavedFilter,
  ISelect,
  ITableDocument,
  IToolbar,
  IToolButton,
  IToolGroup,
  ITotalSum,
  IValue,
  PasswordStrength,
  TRecordValue,
} from '../app/services/api5-service/api.interface';
import {
  IFormModel,
  IListMap,
} from '../common/dynamic-form/dynamic-form.interface';

/**
 * Helper class for get property form doc
 */
export class DocHelper {
  static errorReport?: ErrorHandler;

  /**
   * Get error
   *
   * @param error - error object from doc
   */
  static getError(error?: IErrorResponse): string | undefined {
    if (!error || !error.msg) {
      return;
    }

    let errMsg = '';
    const errObject = error.msg;
    if (Array.isArray(errObject)) {
      errMsg = errObject[0]?.$;
    } else if (errObject.detail?.$) {
      errMsg = errObject.detail.$;
    } else {
      errMsg = errObject.$;
    }
    return errMsg;
  }

  /**
   * Get password strength messgae
   *
   * @param strength - password strength
   * @param doc - doc object
   */
  static getPasswordStrengthMessage(
    strength: PasswordStrength | undefined,
    doc: IDocument,
  ): string | undefined {
    switch (strength) {
      case '0':
        return DocHelper.getMessage('msg_pwcheck_short', doc);
      case '1':
        return DocHelper.getMessage('msg_pwcheck_weak', doc);
      case '2':
        return DocHelper.getMessage('msg_pwcheck_good', doc);
      case '3':
        return DocHelper.getMessage('msg_pwcheck_strong', doc);
      default:
        return undefined;
    }
  }

  /**
   * Get message by name
   *
   * @param name - msg key
   * @param doc - doc object
   */
  static getMessage(name: string | undefined, doc?: IDocument | null): string {
    if (!doc || !name) {
      return '';
    }
    return this.getMessageSet(doc)[name] || '';
  }

  /**
   * Get stream of message by name
   *
   * @param name - msg key
   * @param doc$ - stream of doc object
   */
  static getMessage$(
    name: string,
    doc$: Observable<IDocument>,
  ): Observable<string> {
    return doc$.pipe(
      map(doc => DocHelper.getMessage(name, doc)),
      distinctUntilChanged(),
    );
  }

  /**
   * Get messages
   *
   * @param doc - doc object
   */
  static getMessageSet(doc: IDocument): IMessageSet {
    let messageSet = {};
    const messageSetRaw = doc?.messages;
    // @HACK sometimes doc has several messages object. It's wrong and need delete this a month later
    // and we send error in sentry where this error occurs
    if (Array.isArray(messageSetRaw)) {
      if (DocHelper.errorReport) {
        DocHelper.errorReport.handleError(
          new Error(
            `Error: several messages object in doc. Func: ${doc?.$func}`,
          ),
        );
      }
      messageSetRaw.forEach(msg => {
        messageSet = { ...messageSet, ...msg.msg };
      });
    } else {
      messageSet = messageSetRaw?.msg || {};
    }
    return messageSet;
  }

  static elid(doc?: IDocument | null): string {
    if (!doc) {
      return '';
    }
    return Array.isArray(doc.elid) ? doc.elid[0].$ : doc.elid?.$ || '';
  }

  static plid(doc?: IDocument | null): string {
    if (!doc) {
      return '';
    }
    return Array.isArray(doc.plid) ? doc.plid[0].$ : doc.plid?.$ || '';
  }

  static type(doc: IDocument): TabType {
    return (
      DocHelper.typeFromFunc(String(doc.$func)) ||
      DocHelper.typeFromError(doc.error) ||
      (doc.metadata?.$type as TabType)
    );
  }

  static typeFromError(error: IErrorResponse | undefined): TabType | undefined {
    return error?.$type === 'missed' ? 'notFound' : undefined;
  }

  static typeFromFunc(func: string): TabType | undefined {
    if (func === 'dashboard') {
      return 'dashboard';
    }

    if (func === Func.Branding) {
      return 'branding';
    }

    return undefined;
  }

  static isPinned(doc: IDocument): boolean {
    return DocHelper.type(doc) === 'dashboard' || doc.$pin === 'yes';
  }

  static isHaveMetadata(doc: IDocument): boolean {
    return ['list', 'form', 'report', 'branding', 'helpboard'].includes(
      DocHelper.type(doc),
    );
  }

  static getStringValue(key: string, doc: IDocument): string {
    const value: IValue | IValue[] | undefined = doc[key];

    if (!value) {
      return '';
    }

    if (Array.isArray(value)) {
      const uniqueValues = new Set(
        value.map(v => v.$).filter(v => v !== undefined),
      );
      return Array.from(uniqueValues).join(',');
    } else {
      return value.$ || '';
    }
  }

  /**
   * Get value by key from server (doc)
   *
   * @param key - value key from doc
   * @param doc - form data from server
   * @returns null if there is no such value in doc
   */
  static getValue(key: string, doc: IDocument): string[] | string | null {
    let value = doc[key];
    if (Array.isArray(value)) {
      value = value.map(v => v?.$).filter(v => Boolean(v));
    } else {
      value = value ? value.$ || '' : null;
    }
    return value;
  }

  static getSelectValue(key: string, doc: IDocument): string[] | string {
    const value: IValue | IValue[] | undefined = doc[key];

    if (key === 'lang') {
      if (Array.isArray(value)) {
        return value[0]?.$ || '';
      }
      return value?.$ || '';
    }

    if (Array.isArray(value)) {
      return value.map(v => v.$);
    }
    return value?.$ || '';
  }

  static getFilterValue(
    control: IListFilterBinder,
    doc: IDocument,
  ): string[] | string | undefined {
    if (control.$type === 'checkbox') {
      return;
    }

    const docValue = DocHelper.getValue(control.$name, doc);
    if (!docValue) {
      return control.$type === 'multiple' ? [] : 'null';
    }

    if (control.$type === 'multiple') {
      return Array.isArray(docValue) ? docValue : docValue.split(',');
    }

    return docValue;
  }

  static getTotalSumData(
    control: ITotalSum,
    doc: IDocument,
  ): ICart | undefined {
    return doc[control.$name];
  }

  static getElement(name: string, record: IRecord | IRecord[]): TRecordValue {
    let value = record?.[name];
    if (!value && Array.isArray(record)) {
      value = record[0]?.[name];
      if (Array.isArray(value)) {
        value = value[0];
      }
    }
    return value;
  }

  /**
   * Takes usual value
   *
   * @param name - element name
   * @param record - whole row
   */
  static getElementValue(name: string, record: IRecord | IRecord[]): string {
    const element = this.getElement(name, record);
    return element?.$ || '';
  }

  /**
   * Takes original or usual value
   *
   * @param name - element name
   * @param record - whole row
   */
  static getElementOrigValue(
    name: string,
    record: IRecord | IRecord[],
  ): string {
    const element = this.getElement(name, record);
    return element?.$orig || element?.$ || '';
  }

  static getElementColor(name: string, record: IRecord | IRecord[]): string {
    let element = this.getElement(name, record);
    if (Array.isArray(element)) {
      element = element[0];
    }
    return element?.$color || '';
  }

  static getTParams(doc: IDocument): Params {
    const params = {};
    for (const paramKey in doc.tparams) {
      if (Object.prototype.hasOwnProperty.call(doc.tparams, paramKey)) {
        params[paramKey] = doc.tparams[paramKey]?.$;
      }
    }
    return params;
  }

  /**
   * Get element list form doc
   *
   * @param doc - doc object
   */
  static getElemList(doc: IDocument): IRecord[] {
    const elem = doc?.elem;
    if (Array.isArray(elem)) {
      return elem as IRecord[];
    } else if (elem) {
      return [elem];
    } else {
      return [];
    }
  }

  /**
   * Get toolgrp
   *
   * @param doc - doc object
   */
  static getToolGrp(doc: IDocument): IToolGroup[] {
    return DocHelper.getToolbar(doc)?.toolgrp || [];
  }

  /**
   * Get all toolbar buttons
   *
   * @param doc - doc object
   */
  static getToolButtons(doc: IDocument | null): IToolButton[] {
    return (
      doc?.metadata?.toolbar?.toolgrp
        ?.map(group => group.toolbtn)
        .filter(Boolean)
        .flat() || []
    );
  }

  /**
   * Get filter button
   *
   * @param doc - doc object
   */
  static getFilterButton(doc: IDocument): IToolButton | undefined {
    const buttons = DocHelper.getToolButtons(doc);
    return buttons.find(button => button.$name === 'filter');
  }

  /**
   * Get new button
   *
   * @param doc - doc object
   */
  static getNewButton(doc: IDocument): IToolButton | undefined {
    const buttons = DocHelper.getToolButtons(doc);
    return buttons.find(
      button => button.$name !== 'filter' && button.$type === 'new',
    );
  }

  /**
   * Get saved filters from doc
   *
   * @param doc - doc instance
   */
  static getSavedFilters(doc: IFilterDocument | null): ISavedFilter[] {
    if (!doc) {
      return [];
    }

    return DocHelper.getArrayWrapperValue(doc.saved_filters?.filter);
  }

  /**
   * Get currently applied saved filter name
   *
   * @param doc - filter doc
   */
  static getCurrentSavedFilter(
    doc: IFilterDocument | null,
  ): string | undefined {
    return DocHelper.getSavedFilters(doc).find(
      filter => filter.$current === 'yes',
    )?.$;
  }

  /**
   * Get elid column
   *
   * @param doc - doc object
   */
  static getElidColumn(doc: ITableDocument | null): string {
    return doc?.metadata?.$key || '';
  }

  /**
   * Get the key name of the column
   *
   * @param doc - doc object
   */
  static getKeyName(doc: ITableDocument): string {
    return doc?.metadata?.$keyname || '';
  }

  /**
   * Returns array wrapped given value or original value if its array
   * Use it to get the value from BE data arrays cause you can't be sure
   * won't be an array or one single element
   *
   * @param value - unsafe value in which you unsure array it or not
   */
  static getArrayWrapperValue<T>(
    value: T | T[],
  ): (T extends null | undefined ? never : T)[] {
    return filterNotDefinedItems(Array.isArray(value) ? value : [value]);
  }

  /**
   * Transform list array to list map
   *
   * @param list - list to transform
   */
  static reduceFormList(list: IListOptionList[]): IListMap {
    if (!list?.reduce) {
      return {};
    }
    return list.reduce<IListMap>((acc, cur) => {
      acc[cur.$name] = {
        elem: cur.elem,
        header: DocHelper.getArrayWrapperValue(cur.header),
      };
      return acc;
    }, {});
  }

  /**
   * Get toolbar meta from tab
   *
   * @param doc - doc instance
   */
  static getToolbar(doc: IDocument | null): IToolbar | undefined {
    if (!doc?.metadata?.toolbar?.toolgrp) {
      return;
    }

    const toolbarCopy: IToolbar = { ...doc.metadata.toolbar };

    // @HACK 'back' button already exists in page-header.component, 'filter' button should not be rendered at all
    toolbarCopy.toolgrp = (toolbarCopy.toolgrp || [])
      .filter(group => Boolean(group))
      .map(group => {
        if (group.toolbtn) {
          return {
            ...group,
            toolbtn: group.toolbtn.filter(
              t => t.$name !== 'back' && t.$name !== 'filter',
            ),
          };
        } else {
          return group;
        }
      });

    const isHaveSomeButtons = toolbarCopy.toolgrp.some(
      group => group.toolbtn?.length,
    );

    return isHaveSomeButtons ? toolbarCopy : undefined;
  }

  static getToolbar$(
    doc$: Observable<IDocument>,
  ): Observable<IToolbar | undefined> {
    return doc$.pipe(
      map(doc => DocHelper.getToolbar(doc)),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
    );
  }

  static getWizardSteps(doc: IFormDocument): IWizardStepUi[] | undefined {
    const steps = DocHelper.getArrayWrapperValue(doc.steps?.step);
    if (steps.length === 0) {
      return;
    }

    const pageFunc = doc.$func;
    const activeElIndex = steps.findIndex(step => step.$name === pageFunc);

    return steps.map<IWizardStepUi>((step, idx) => ({
      msg: step.$,
      func: step.$name,
      disabled: idx >= activeElIndex || step.$unavailable === 'yes',
      hidden: step.$hidden === 'yes',
      active: idx === activeElIndex,
      isPrevious: idx === activeElIndex - 1,
      isNext: idx === activeElIndex + 1,
      child: step.$child,
    }));
  }

  static getWizardSteps$(
    doc$: Observable<IFormDocument>,
  ): Observable<IWizardStepUi[] | undefined> {
    return doc$.pipe(
      map(doc => DocHelper.getWizardSteps(doc)),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
    );
  }

  static getToolbarBackButton(doc: IDocument): IToolButton | undefined {
    const toolbar = doc.metadata?.toolbar;

    const backBtnGroup = toolbar?.toolgrp?.find(
      group => group.$name === 'back',
    );
    const backButton = backBtnGroup?.toolbtn?.[0];
    return backButton;
  }

  /**
   * Get toolbar meta from tab contextmenu
   *
   * @param doc - doc instance
   */
  static getContextMenu(doc: IDocument | null): IToolbar | undefined {
    return doc?.metadata?.contextmenu || DocHelper.getToolbar(doc);
  }

  /**
   * Check is doc have active filter
   *
   * @param doc - doc instance
   */
  static isFilterActive(doc: ITableDocument): boolean {
    return Boolean(doc.p_filter);
  }

  /**
   * Get applyed filter msg
   *
   * @param doc - doc instance
   */
  static getApplyedFilterMsg(doc: ITableDocument): string {
    return doc.p_filter?.$ || '';
  }

  /**
   * Get all notification-like banners from doc
   *
   * @param doc - doc
   */
  static getNotificationBanners(doc: IDocument | null): INotificationBanner[] {
    return (
      (doc?.banner as INotificationBanner[])?.filter(b => !b.$pinned) || []
    );
  }

  /**
   * Get all normal banners from doc
   *
   * @param doc - doc
   */
  static getNormalBanners(doc: IDocument | null): INormalBanner[] {
    return (
      (doc?.banner as INormalBanner[])?.filter(b => b.$pinned === 'yes') || []
    );
  }

  /**
   * Get all plain hints for select
   *
   * @param doc - doc
   * @param select - select
   */
  static getPlainHintsForSelect(
    doc: IFormDocument,
    select: ISelect,
  ): Record<string, string> {
    const optionsList =
      doc.slist?.find(item => item.$name === select.$name)?.val || [];

    return optionsList.reduce<Record<string, string>>((plainHints, option) => {
      const key = `hint_${select.$name}__${option.$key}`;
      plainHints[key] = DocHelper.getMessage(key, doc);
      return plainHints;
    }, {});
  }

  /**
   * Get all plain hints for checkbox
   *
   * @param doc - doc
   * @param input - input with type checkbox
   */
  static getPlainHintsForCheckbox(
    doc: IDocument,
    input: ICheckbox,
  ): Record<string, string> {
    const keyOn = `hint_${input.$name}__on`;
    const keyOff = `hint_${input.$name}__off`;
    return {
      [keyOn]: DocHelper.getMessage(keyOn, doc),
      [keyOff]: DocHelper.getMessage(keyOff, doc),
    };
  }

  /**
   * Is new document has different controls than old doc
   *
   * @param prevDoc - prev doc
   * @param nextDoc - next doc
   */
  static isDocControlsChanged(
    prevDoc: IFormDocument | undefined,
    nextDoc: IFormDocument,
  ): boolean {
    if (!prevDoc || nextDoc.$func !== prevDoc.$func) {
      return true;
    }

    const oldForm = DocHelper.getFormControls(prevDoc);
    const newForm = DocHelper.getFormControls(nextDoc);

    return !deepEqual(oldForm, newForm);
  }

  /**
   * Get form page list
   *
   * @param doc - doc object
   */
  static getFormPages(doc: IFormDocument): IFormPage[] {
    return doc.metadata?.form?.page || [];
  }

  static getFormControlsFromPage(page: IFormPage): IControl[] {
    return page.field
      .map(field => DocHelper.getFormControlsFromField(field))
      .flat();
  }

  static getFormControlsFromField(field: IField): IControl[] {
    return [
      ...(field.captcha ? [field.captcha] : []),
      ...(field.reflect ? field.reflect : []),
      ...(field.input ? field.input : []),
      ...(field.select ? field.select : []),
      ...(field.datetime ? field.datetime : []),
      ...(field.textarea ? field.textarea : []),
      ...(field.link ? field.link : []),
      ...(field.list ? field.list : []),
      ...(field.htmldata ? field.htmldata : []),
      ...(field.textdata ? field.textdata : []),
      ...(field.frame ? field.frame : []),
      ...(field.slider ? field.slider : []),
      ...(field.tree ? field.tree : []),
      ...(field.ticket ? field.ticket : []),
    ];
  }

  static getFormFields(doc: IFormDocument): IField[] {
    if (!doc.metadata?.form) {
      return [];
    }

    const pageFields = (
      doc.metadata.form.page?.map(page => page.field) ||
      (doc.metadata.form.field ? [doc.metadata.form.field] : [[]])
    )
      .filter(field => Boolean(field))
      .flat();

    return pageFields;
  }

  static getFormControls(doc: IFormDocument): IControl[] {
    if (!doc.metadata?.form) {
      return [];
    }

    const pageFields = DocHelper.getFormFields(doc);

    const controls = pageFields
      .map(field => DocHelper.getFormControlsFromField(field))
      .flat();

    return controls;
  }

  static getFormModel(doc: IDocument | null): IFormModel {
    if (!doc) {
      return {};
    }
    const typedForm = getTypedForm(doc);

    if (!typedForm) {
      return {};
    }

    const typedControls = getTypedControls(typedForm);
    return typedControls.reduce<IFormModel>((model, typedControl) => {
      if ('value' in typedControl && typedControl.value !== undefined) {
        model[typedControl.control.$name] = typedControl.value;
      }

      return model;
    }, {});
  }

  /**
   * Get logo url
   *
   * @param doc - desktop metadata
   * @param localDir - custom assets folder
   * @param logo - custom logo name
   */
  static getLogoUrl(
    doc: IDesktopDocument,
    localDir?: string,
    logo?: string,
  ): string {
    return `${doc.$host}${doc.$theme}${localDir || doc.$localdir}${
      logo || doc.$logo
    }`;
  }

  /**
   * Get login logo url
   *
   * @param doc - desktop metadata
   * @param localDir - custom assets folder
   * @param logo - custom logo name
   */
  static getLoginLogoUrl(
    doc: IDesktopDocument,
    localDir?: string,
    logo?: string,
  ): string {
    return `${doc.$host}${doc.$theme}${localDir || doc.$localdir}${
      logo || 'login-logo-billmgr.svg'
    }`;
  }

  /**
   * Check that desktop interface is in setup mode
   *
   * @param doc
   */
  static isSetupMode(doc: IDesktopDocument): boolean {
    return doc.first_start?.$ === 'on';
  }

  /**
   * Get start page from desktop. Start page used for opening initial tab. Usually it's table tab
   *
   * @param doc
   */
  static getStartPage(doc: IDesktopDocument): string {
    return doc.mainmenu.$startpage;
  }

  /**
   * Get start form from desktop. Start form used for opening initial tab. Usually it's form tab
   * Can be used in combination with startpage. If startpage also provided, then startform should open child tab of startpage
   *
   * @param doc
   */
  static getStartForm(doc: IDesktopDocument): string | undefined {
    return doc.mainmenu.$startform;
  }
}
